import { Fragment, useMemo } from "react";

import { Text } from "../../components/Text";
import { detectMessageLinks } from "../utils/detectMessageLinks";
import { ChatLink } from "./ChatLink";

interface ChatMessageBodyProps {
  /** The (already parsed) message text to render. */
  text: string;
  /**
   * Incoming messages render on a light bubble with dark text; outgoing
   * messages render on the brand-colored bubble with inverted text. This
   * drives both the text color and the link color.
   */
  isIncoming: boolean;
}

/**
 * Renders a chat message body, turning any URLs in the text into clickable
 * links. Newlines are preserved (`whiteSpace: pre-wrap`) to match the rest of
 * the message bubble.
 *
 * Links open via {@link ChatLink} so they respect the Capacitor-mobile vs web
 * split (in-app browser on native, new tab on web) instead of calling
 * `window.open` directly.
 */
export function ChatMessageBody(props: ChatMessageBodyProps) {
  const { text, isIncoming } = props;
  // Memoized so we don't re-parse on every re-render (the message log re-renders
  // frequently, e.g. on read-status updates, across many messages at once).
  const segments = useMemo(() => detectMessageLinks(text), [text]);

  // Stable per-segment keys derived from each segment's offset in the string.
  // Segments partition `text`, so offsets are unique and never reorder.
  let offset = 0;

  return (
    <Text
      variant="body2"
      sx={(theme) => ({
        color: isIncoming ? theme.palette.base?.strong : theme.palette.base?.inverted?.strong,
        whiteSpace: "pre-wrap",
      })}
    >
      {segments.map((segment) => {
        const key = `${offset}-${segment.type}`;
        offset += segment.content.length;

        if (segment.type === "link") {
          return (
            <ChatLink key={key} href={segment.href} isIncoming={isIncoming}>
              {segment.content}
            </ChatLink>
          );
        }

        return <Fragment key={key}>{segment.content}</Fragment>;
      })}
    </Text>
  );
}
